import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router';
import { Back } from '../../assets/images';

// Left arrow icon component using Back icon
const LeftArrowIcon = ({ onClick }) => <Back />;

// Right arrow icon component
const RightArrowIcon = ({ onClick }) => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M7.5 5L12.5 10L7.5 15"
            stroke="#9B96A2"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// Close icon component
const CloseIcon = ({ onClick }) => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M15 5L5 15M5 5L15 15"
            stroke="#9B96A2"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// Menu icon component
const MenuIcon = ({ onClick }) => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M3 6H17M3 10H17M3 14H17"
            stroke="#9B96A2"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// Search icon component
const SearchIcon = ({ onClick }) => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <circle
            cx="9"
            cy="9"
            r="8"
            stroke="#9B96A2"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M21 21L16.65 16.65"
            stroke="#9B96A2"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const StyledNavigation = styled(Box)(({ theme }) => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 0',
    width: '100%',
    minHeight: '40px', // title 여부와 상관없이 최소 높이 보장
    height: '40px', // 고정 높이 설정
}));

const Navigation = ({
    title = '',
    showLeftIcon = true,
    showRightIcon = false,
    rightIconType = 'none', // 'none', 'arrow', 'close', 'menu', 'search', 'custom'
    customRightIcon = null,
    onLeftIconClick,
    onRightIconClick = () => { },
    leftIconType = 'arrow', // 'arrow', 'close', 'menu', 'custom'
    customLeftIcon = null,
    // Multiple right icons support
    customRightIcons = null,
    sx = {},
    ...props
}) => {
    const theme = useTheme();
    const navigate = useNavigate();

    // 기본값으로 navigate(-1) 설정
    const handleLeftIconClick = onLeftIconClick || (() => navigate(-1));
    const renderLeftIcon = () => {
        if (!showLeftIcon) return null;

        if (customLeftIcon) {
            return (
                <IconButton
                    onClick={handleLeftIconClick}
                    sx={{
                        position: 'absolute',
                        left: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        padding: '4px',
                        '&:hover': {
                            backgroundColor: theme.palette.figma.white[20],
                        },
                    }}
                >
                    {customLeftIcon}
                </IconButton>
            );
        }

        const iconMap = {
            arrow: <LeftArrowIcon />,
            close: <CloseIcon />,
            menu: <MenuIcon />,
        };

        return (
            <IconButton
                onClick={handleLeftIconClick}
                sx={{
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    padding: '4px',
                    '&:hover': {
                        backgroundColor: theme.palette.figma.white[20],
                    },
                }}
            >
                {iconMap[leftIconType] || <LeftArrowIcon />}
            </IconButton>
        );
    };

    const renderRightIcon = () => {
        if (!showRightIcon) return null;

        if (customRightIcon) {
            return (
                <IconButton
                    onClick={onRightIconClick}
                    sx={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        padding: '0px',
                        '&:hover': {
                            backgroundColor: theme.palette.figma.white[20],
                        },
                    }}
                >
                    {customRightIcon}
                </IconButton>
            );
        }

        const iconMap = {
            arrow: <RightArrowIcon />,
            close: <CloseIcon />,
            menu: <MenuIcon />,
            search: <SearchIcon />,
        };

        return (
            <IconButton
                onClick={onRightIconClick}
                sx={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    padding: '4px',
                    '&:hover': {
                        backgroundColor: theme.palette.figma.white[20],
                    },
                }}
            >
                {iconMap[rightIconType] || <RightArrowIcon />}
            </IconButton>
        );
    };

    const renderRightIcons = () => {
        if (customRightIcons) return null;

        return (
            <Box sx={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
            }}>
                {customRightIcons.map((iconData, index) => {
                    const { icon, onClick, type } = iconData;

                    if (type === 'custom' && icon) {
                        return (
                            <IconButton
                                key={index}
                                onClick={onClick}
                                sx={{
                                    padding: '0px',
                                    '&:hover': {
                                        backgroundColor: theme.palette.figma.white[20],
                                    },
                                }}
                            >
                                {icon}
                            </IconButton>
                        );
                    }

                    const iconMap = {
                        arrow: <RightArrowIcon />,
                        close: <CloseIcon />,
                        menu: <MenuIcon />,
                        search: <SearchIcon />,
                    };

                    return (
                        <IconButton
                            key={index}
                            onClick={onClick}
                            sx={{
                                padding: '0px',
                                '&:hover': {
                                    backgroundColor: theme.palette.figma.white[20],
                                },
                            }}
                        >
                            {iconMap[type] || <RightArrowIcon />}
                        </IconButton>
                    );
                })}
            </Box>
        );
    };

    return (
        <StyledNavigation sx={sx} {...props}>
            {renderLeftIcon()}

            <Typography
                sx={{
                    fontSize: '16px',
                    fontWeight: 500,
                    letterSpacing: '-0.48px',
                    textAlign: 'center',
                    lineHeight: '20px',
                    minHeight: '20px', // title이 없어도 공간 유지
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {title}
            </Typography>

            {customRightIcons ? renderRightIcons() : renderRightIcon()}
        </StyledNavigation>
    );
};

export default Navigation;
